import { Component, OnInit, ViewChild } from '@angular/core';
import { AgentsService } from '../utils/services/agents.service';
import { AgentStatus } from '../utils/models/agent.model';
import { DialogService } from '../dialogservice.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, delay, finalize, mergeMap, tap, toArray } from 'rxjs/operators';
import moment from 'moment';
import { KeycloakService } from 'keycloak-angular';
import { SharedService } from '../utils/services/shared.service';
import { forkJoin, from } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProjectsService } from '../utils/services/projects.service';
import { MaterialModule } from '../material.module';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { AgentsFilterComponent } from './agents-filter/agents-filter.component';
import { AgentviewComponent } from '../agentview/agentview.component';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../spinner/spinner.component';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { RuleModel } from '@syncfusion/ej2-angular-querybuilder';
import { PartnersService } from '../utils/services/partners.service';
import { AgentsSearchService } from '../utils/services/agents-search.service';

@Component({
  selector: 'app-archived-agents',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    CheckBoxModule,
    DropDownListModule,
    AgentsFilterComponent,
    AgentviewComponent,
    SpinnerComponent
  ],
  templateUrl: './archived-agents.component.html',
  styleUrl: './archived-agents.component.scss'
})
export class ArchivedAgentsComponent implements OnInit {
  @ViewChild('agentsfilter') agentsFilter!: AgentsFilterComponent;

  archivedAgents: any = [];
  filteredAgents: any = [];
  public data: any = {};
  search = new FormControl('');
  isAllAgent: boolean = false;
  partnerId: string = '';
  partnerName: string = '';
  allAgents: any = [];
  uniquePartnerIdArray: any = [];
  isSpinnerActive: any = false;
  dropdownFields: any = { text: 'name', value: 'id' };
  b2b: boolean = false;

  currentRole: string = '';
  currentUser: string = '';

  /**
   * Map partnerId to partnerName on provided agent list
   */
  private applyPartnerNames(agentList: any[]): void {
    if (!this.partnersService.partners?.length) {
      return
    }
    agentList.forEach(agent => {
      // if agent already contains partnerName, use it directly
      if (agent.partnerName) {
        agent.partnerCompany = agent.partnerName
        return
      }
      // otherwise fall back to lookup by partnerId
      const partner = this.partnersService.partners.find(p => p.id === agent.partnerId || p.partnerName === agent.partnerCompany)
      agent.partnerCompany = partner?.partnerName ?? agent.partnerCompany ?? ''
    })
  }

  constructor(
    private agentsService: AgentsService,
    public dialogService: DialogService,
    private sharedService: SharedService,
    private snackBar: MatSnackBar,
    private projectsService: ProjectsService,
    public partnersService: PartnersService,
    private readonly keycloak: KeycloakService,
    private readonly agentsSearchService: AgentsSearchService
  ) {
  }

  ngOnInit(): void {
    this.projectsService.getAllProjects('', 'Admin').subscribe({
      next: (res: any) => {
        this.projects = res;
      },
      error: (error) => {
        console.error('Error fetching projects: ', error);
      },
    });

    const tokenData = this.keycloak.getKeycloakInstance().tokenParsed;
    const userRoles = tokenData?.realm_access?.roles;
    this.currentUser = tokenData?.['email'];

    if (userRoles?.includes('Client')) {
      this.currentRole = 'Client';
    } else if (userRoles?.includes('Partner')) {
      this.currentRole = 'Partner';
    } else if (userRoles?.includes('Admin')) {
      this.currentRole = 'Admin';
    }

    const status = AgentStatus.ApplicantArchived;
    this.isSpinnerActive = true;
    this.agentsService.getAgentByStatus(status).subscribe((res) => {
      this.archivedAgents = res;
      this.archivedAgents.forEach((agent: any) => {
        const { project, status, createdAt, agentProfilePhoto } = agent;

        agent.projectName = project ? project.name : '';
        agent.statusName = this.sharedService.statuses[status].display;
        agent.createdAt = moment(createdAt).format('DD.MM.YYYY');
        agent.profileImageSrc = agentProfilePhoto?.photoBlob
          ? this.agentsService.convertImagetoUrl(agentProfilePhoto.photoBlob)
          : null;
          
      });
      this.applyPartnerNames(this.archivedAgents)
      this.isSpinnerActive = false
      this.getArchivedAgentsPhoto()
      this.filteredAgents = this.archivedAgents
      this.getAllAgent()
    });

    let userId = '';
    if (userRoles?.includes('Partner')) {
      userId = tokenData?.['sub'] || '';
    }

    this.partnersService.fetchAll(() => {
      // partner list loaded – enrich existing agent arrays
      this.applyPartnerNames(this.archivedAgents)
      this.applyPartnerNames(this.allAgents)
      const p = this.partnersService.partners.find(partner => partner.userId === userId);
      this.partnerId = p?.id || '';
      this.partnerName = p?.partnerName ?? '';
    });

    this.search.valueChanges.pipe(debounceTime(300)).subscribe((newValue: string | null) => this.applyAllFilters(newValue))
  }

  /**
   * Applies all active filters in a single, consistent pipeline.
   * Order: choose data source (all vs archived) -> optional b2b filter -> text search -> rule builder rules
   * This avoids race conditions where async loaders overwrite filtered lists.
   * @param searchOverride optional search term to apply instead of current FormControl value
   */
  private applyAllFilters(searchOverride: string | null = null): void {
    const term = (searchOverride ?? this.search.value ?? '').toString()

    let base = this.isAllAgent ? this.allAgents : this.archivedAgents
    if (this.b2b) {
      base = base.filter((agent: any) => !!agent?.b2bCapable)
    }

    let result = term && term !== '' ? this.agentsSearchService.filterAgents(base, term) : [...base]

    // Apply QueryBuilder rules if available
    if (this.agentsFilter && typeof this.agentsFilter.getFilter === 'function') {
      const ruleModel = this.agentsFilter.getFilter()
      if (ruleModel && Array.isArray(ruleModel.rules) && ruleModel.rules.length > 0) {
        result = result.filter((agent: any) => this.applyFilter(agent, ruleModel))
      }
    }

    this.filteredAgents = result
  }

  public filterAgentsByValue(newValue: string | null): void {
    // Delegate to unified filter pipeline
    this.applyAllFilters(newValue)
  }

  public filter() {
    // Re-apply all filters including the current QueryBuilder rules
    this.applyAllFilters(this.search.value)
  }

  private applyFilter(agent: any, ruleModel: RuleModel): boolean {
    let result = ruleModel.condition === 'and';

    const stack = [ruleModel];
    const parents = new Map<RuleModel, RuleModel>();

    while (stack.length > 0) {
      const current = stack.shift();

      if (!current) {
        continue;
      }

      if (!current.rules) {
        continue;
      }

      const ruleListResult = this.applyRuleList(agent, current);

      const parent = parents.size > 0 ? parents.get(current)! : current;
      result = parent.condition === 'and' ? result && ruleListResult : result || ruleListResult;

      if (current.rules) {
        for (const rule of current.rules) {
          if (rule.rules) {
            stack.push(rule);
          }
          parents.set(rule, current);
        }
      }
    }

    return result;
  }

  private applyRuleList(agent: any, ruleModel: RuleModel): boolean {
    const condition = ruleModel.condition!;
    const rules = ruleModel.rules!;

    let result = condition === 'and';

    for (const rule of rules) {
      if (rule.condition) {
        continue;
      }

      const field = rule.field!;
      const operator = rule.operator!;
      const type = rule.type!;
      const value = rule.value!;

      let subResult = false;

      switch (operator) {
        case 'startswith':
          {
            const v = agent[field]
            subResult = typeof v === 'string' && typeof value === 'string' && v.startsWith(value)
          }
          break;
        case 'notstartswith':
          {
            const v = agent[field]
            subResult = !(typeof v === 'string' && typeof value === 'string' && v.startsWith(value))
          }
          break;
        case 'endswith':
          {
            const v = agent[field]
            subResult = typeof v === 'string' && typeof value === 'string' && v.endsWith(value)
          }
          break;
        case 'notendswith':
          {
            const v = agent[field]
            subResult = !(typeof v === 'string' && typeof value === 'string' && v.endsWith(value))
          }
          break;
        case 'contains':
          {
            const v = agent[field]
            subResult = typeof v === 'string' && typeof value === 'string' && v.includes(value)
          }
          break;
        case 'notcontains':
          {
            const v = agent[field]
            subResult = !(typeof v === 'string' && typeof value === 'string' && v.includes(value))
          }
          break;
        case 'equal':
          if (type === 'date') {
            const av = new Date(agent[field]).getTime()
            const bv = new Date(value as string).getTime()
            subResult = !isNaN(av) && !isNaN(bv) && av === bv
          } else if (value === 'Keine' || value === 'Schlecht' || value === 'Mittel' || value === 'Gut') {
            switch (value) {
              case 'Keine':
                subResult = agent[field] === 'None'
                break;
              case 'Schlecht':
                subResult = agent[field] === 'Bad'
                break;
              case 'Mittel':
                subResult = agent[field] === 'Medium'
                break;
              case 'Gut':
                subResult = agent[field] === 'Good'
                break;
            }
          } else if (value === 'Ja' || value === 'Nein') {
            subResult = agent[field] === (value === 'Ja' ? 'true' : 'false')
          } else {
            subResult = agent[field] === value
          }
          break;
        case 'notequal':
          if (type === 'date') {
            const av = new Date(agent[field]).getTime()
            const bv = new Date(value as string).getTime()
            subResult = !isNaN(av) && !isNaN(bv) && av !== bv
          } else if (value === 'Keine' || value === 'Schlecht' || value === 'Mittel' || value === 'Gut') {
            switch (value) {
              case 'Keine':
                subResult = agent[field] !== 'None'
                break;
              case 'Schlecht':
                subResult = agent[field] !== 'Bad'
                break;
              case 'Mittel':
                subResult = agent[field] !== 'Medium'
                break;
              case 'Gut':
                subResult = agent[field] !== 'Good'
                break;
            }
          } else if (value === 'Ja' || value === 'Nein') {
            subResult = agent[field] !== (value === 'Ja' ? 'true' : 'false')
          } else {
            subResult = agent[field] !== value
          }
          break;
        case 'in':
          subResult = Array.isArray(value as any[]) && (value as any[]).includes(agent[field] as any)
          break;
        case 'notin':
          subResult = Array.isArray(value as any[]) && !(value as any[]).includes(agent[field] as any)
          break;
        case 'isempty':
          subResult = agent[field] === ''
          break;
        case 'isnotempty':
          subResult = agent[field] !== ''
          break;
        case 'isnull':
          subResult = agent[field] === null
          break;
        case 'isnotnull':
          subResult = agent[field] !== null
          break;

        case 'greaterthanorequal':
          if (type === 'date') {
            {
              const av = new Date(agent[field]).getTime()
              const bv = new Date(value as string).getTime()
              subResult = !isNaN(av) && !isNaN(bv) && av >= bv
            }
          } else if (type === 'number') {
            subResult = typeof agent[field] === 'number' && typeof value === 'number' && agent[field] >= value
          }
          break;
        case 'greaterthan':
          if (type === 'date') {
            {
              const av = new Date(agent[field]).getTime()
              const bv = new Date(value as string).getTime()
              subResult = !isNaN(av) && !isNaN(bv) && av > bv
            }
          } else if (type === 'number') {
            subResult = typeof agent[field] === 'number' && typeof value === 'number' && agent[field] > value
          }
          break;
        case 'between':
          if (type === 'date') {
            const a = new Date(agent[field]).getTime()
            const v0 = new Date((value as string[])[0]).getTime()
            const v1 = new Date((value as string[])[1]).getTime()
            subResult = !isNaN(a) && !isNaN(v0) && !isNaN(v1) && a >= v0 && a <= v1
          } else if (type === 'number') {
            const v0 = (value as number[])[0]
            const v1 = (value as number[])[1]
            subResult = typeof agent[field] === 'number' && typeof v0 === 'number' && typeof v1 === 'number' && agent[field] >= v0 && agent[field] <= v1
          }
          break;
        case 'notbetween':
          if (type === 'date') {
            const a = new Date(agent[field]).getTime()
            const v0 = new Date((value as string[])[0]).getTime()
            const v1 = new Date((value as string[])[1]).getTime()
            subResult = !isNaN(a) && !isNaN(v0) && !isNaN(v1) && (a < v0 || a > v1)
          } else if (type === 'number') {
            const v0 = (value as number[])[0]
            const v1 = (value as number[])[1]
            subResult = typeof agent[field] === 'number' && typeof v0 === 'number' && typeof v1 === 'number' && (agent[field] < v0 || agent[field] > v1)
          }
          break;
        case 'lessthan':
          if (type === 'date') {
            const av = new Date(agent[field]).getTime()
            const bv = new Date(value as string).getTime()
            subResult = !isNaN(av) && !isNaN(bv) && av < bv
          } else if (type === 'number') {
            subResult = typeof agent[field] === 'number' && typeof value === 'number' && agent[field] < value
          }
          break;
        case 'lessthanorequal':
          if (type === 'date') {
            const av = new Date(agent[field]).getTime()
            const bv = new Date(value as string).getTime()
            subResult = !isNaN(av) && !isNaN(bv) && av <= bv
          } else if (type === 'number') {
            subResult = typeof agent[field] === 'number' && typeof value === 'number' && agent[field] <= value
          }
          break;
        
        case 'equalorbetter':
          if (value === 'Keine' || value === 'Schlecht') {
            subResult = agent[field] === 'None' || agent[field] === 'Bad' || agent[field] === 'Medium' || agent[field] === 'Good';
          } else if (value === 'Mittel') {
            subResult = agent[field] === 'Medium' || agent[field] === 'Good';
          } else if (value === 'Gut') {
            subResult = agent[field] === 'Good';
          }
      }

      result = condition === 'and' ? result && subResult : result || subResult;

      if (condition === 'or' && result) {
        break;
      }
    }

    return result;
  }

  onClickAgent(agent: any) {
    let project = null;
  
    try {
      const history = JSON.parse(agent.projectHistory || '[]');
      if (Array.isArray(history) && history.length > 0) {
        project = history[0]; 
      }
    } catch (e) {
      console.error("Failed to parse projectHistory:", e);
    }
  
    this.data = {
      agentId: agent.id,
      projectId: project?.id || null,
      projectName: project?.name || '',
      coloumnName: agent.status,
      ...agent,
    };
    this.dialogService.showAgentView = true;
  }
  onClose(event: any) {
    this.archivedAgents = this.archivedAgents.filter(
      (agent: any) => agent.id != this.data.agentId
    );
    this.filteredAgents = this.archivedAgents;
  }

  

  public onToggleSwitch(): void {
    if (this.isAllAgent) {
      // Toggle switched to show ALL agents – fetch fresh list from backend
      this.getAllAgent()
    } else {
      // Toggle switched to show ARCHIVED agents – fetch fresh archived list
      const status = AgentStatus.ApplicantArchived
      this.agentsService.getAgentByStatus(status).subscribe(res => {
        this.archivedAgents = res
        this.archivedAgents.forEach((agent: any) => {
          agent.projectName = agent.project ? agent.project.name : ''
          agent.statusName = this.sharedService.statuses[agent.status].display
          agent.createdAt = moment(agent.createdAt).format('DD.MM.YYYY')
          agent.profileImageSrc = agent?.agentProfilePhoto?.photoBlob
            ? this.agentsService.convertImagetoUrl(agent.agentProfilePhoto.photoBlob)
            : null
            
        })
        this.applyPartnerNames(this.archivedAgents)
        this.getArchivedAgentsPhoto()
        // re-apply current search and rule filters
        this.applyAllFilters(this.search.value)
      })
    }
    // No manual FormControl emission to avoid duplicate filtering
  }

  fetchPartnerInfo() {
    const partnerObservables = this.uniquePartnerIdArray.map((partnerId: any) =>
      this.agentsService.getPartnerInfo(partnerId)
    );
    forkJoin(partnerObservables).subscribe({
      next: (partnerInfos: any) => {
        const partnerInfoMap: any = {};

        // Map partnerId to its fetched info
        this.uniquePartnerIdArray.forEach((id: any, index: any) => {
          partnerInfoMap[id] = partnerInfos[index];
        });

        this.allAgents = this.allAgents.map((item: any) => ({
          ...item,
          partnerLastName: partnerInfoMap[item.partnerId]?.lastName,
          partnerFirstName: partnerInfoMap[item.partnerId]?.firstName,
        }));
      },
      error: (error: any) => {
        console.error('Error fetching partner info:', error);
      },
    });
  }

  getAllAgent() {
    this.agentsService.getAllAgentsByUserRole('').subscribe((res) => {
      this.allAgents = res;
      this.allAgents.forEach((agent: any) => {
        agent.projectName = agent.project ? agent.project.name : '';
        agent.statusName = this.sharedService.statuses[agent.status].display;
        agent.createdAt = moment(agent.createdAt).format('DD.MM.YYYY');
        if (agent?.agentProfilePhoto?.photoBlob) {
          agent.profileImageSrc = this.agentsService.convertImagetoUrl(
            agent.agentProfilePhoto.photoBlob
          );
        } else {
          agent.profileImageSrc = null;
        }
        if (!this.uniquePartnerIdArray.includes(agent.partnerId)) {
          this.uniquePartnerIdArray.push(agent.partnerId);
        }
      });
      this.applyPartnerNames(this.allAgents)
      this.getAllAgentsPhoto()
      this.applyAllFilters(this.search.value)
    });
  }

  public isMediaPlay: boolean = false;
  public selectedAudioUrl: any;
  public selectedVideoUrl: any;

  openMediaModal(event: Event, id: number) {
    event.stopPropagation();
    this.getSelectedAgentsSelectedMedia(id);
  }

  getSelectedAgentsSelectedMedia(id: number) {
    this.isSpinnerActive = true;
    const agent: any = this.filteredAgents.find((agent: any) => agent.id === id);
    const videoBlob = agent?.videoFiles.find(
      (video: any) => video.status === 'accepted'
    );
    const voiceBlob = agent?.voiceFiles.find(
      (voice: any) => voice.status === 'accepted'
    );

    if (videoBlob) {
      // this.agentsService.getVideoById(videoBlob.id).subscribe((res: any) => {
        // this.convertToVideoBlobUrl(res.videoBlob.data);
      this.selectedVideoUrl = this.agentsService.getVideoStreamUrl(videoBlob.id);
        if (voiceBlob) {
          // this.agentsService
          //   .getAudioById(voiceBlob.id)
          //   .subscribe((res: any) => {
              // this.convertToAudioBlobUrl(res.voiceBlob.data);
              this.selectedAudioUrl = this.agentsService.getAudioStreamUrl(voiceBlob.id);
              this.isMediaPlay = true;
              this.isSpinnerActive = false;
            // });
        } else {
          this.isMediaPlay = true;
          this.isSpinnerActive = false;
        }
      // });
    } else if (voiceBlob) {
      // this.agentsService.getAudioById(voiceBlob.id).subscribe((res: any) => {
      //   this.convertToAudioBlobUrl(res.voiceBlob.data);
        this.selectedAudioUrl = this.agentsService.getAudioStreamUrl(voiceBlob.id);
        this.isMediaPlay = true;
        this.isSpinnerActive = false;
      // });
    }
    if (!videoBlob && !voiceBlob) {
      this.snackBar.open('Nicht akzeptiert Audiodatei oder Videodatei.', '', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
      this.isSpinnerActive = false;
    }
  }

  isPlayIconVisible(entry: any) {
    switch (entry.status) {
      case 'WaitingForAgent':
      case 'ApplicantGetsSuggested':
      case 'ApplicantRejected':
      case 'AppointmentScheduled':
      case 'ApplicantAccepted':
      case 'ApplicantConfirmed':
        return entry.voiceFiles.length > 0 || entry.videoFiles.length > 0;
      default:
        return false;
    }
  }

  getPlayIconColor(entry: any) {
    if (entry.voiceFiles.some((obj: any) => obj.status === 'accepted') || entry.videoFiles.some((obj: any) => obj.status === 'accepted')) {
      return '#24b89b';
    } else if (
      entry.voiceFiles.every((obj: any) => obj.status !== 'accepted')
    ) {
      return '#af1c3d';
    } else {
      return '#6c6c6c';
    }
    // if (entry.voiceFiles.some((obj: any) => obj.isPlayed === false)) {
    //   return '#af1c3d';
    // } else if (entry.voiceFiles.every((obj: any) => obj.isPlayed === true)) {
    //   return '#24b89b';
    // } else {
    //   return '#6c6c6c';
    // }
  }

  public selectedAgents: Map<number, any> = new Map<number, any>();

  onSelectAgent(args: any, agent: any) {
    args.event.stopPropagation();
    if (args.checked) {
      this.selectedAgents.set(agent.id, agent);
    } else {
      this.selectedAgents.delete(agent.id);
    }
  }

  public projects: any = [];
  public selectedProject: any = null;

  onMoveToProject() {
    if (!this.selectedProject) {
      this.snackBar.open('Bitte wählen Sie ein Projekt aus.', '', {
        duration: 4000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
      return;
    }

    const reasonToMoveInOtherProject = 'Mehrere Agenten wurden in ein anderes Projekt verschoben';

    from(this.selectedAgents).pipe(
      mergeMap(agent => this.agentsService.agentMoveToOtherProject(agent[0], { projectId: this.selectedProject, reasonToMoveInOtherProject: reasonToMoveInOtherProject })),
      tap(receivedAgent => {
        const text = `Verschieben in ein anderes Projekt mit Grund "${reasonToMoveInOtherProject}"`;
        this.sharedService.logAgentMoment(receivedAgent, this.currentUser, this.currentRole, text);
      }),
      toArray(),
      tap(() => {
        this.snackBar.open('Alle Agenten wurden erfolgreich verschoben!', '', {
          duration: 4000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
        });
      }),
      delay(1000),
      finalize(() => window.location.reload())
    ).subscribe();
  }

  adjustImage(image: HTMLImageElement): void {
    if (image.naturalWidth === image.naturalHeight) {
      image.style.width = '100%';
      image.style.height = '100%';
    } else if (image.naturalWidth < image.naturalHeight) {
      image.style.width = '100%';
      image.style.height = 'auto';
    } else {
      image.style.width = 'auto';
      image.style.height = '100%';
    }
  }

  /**
   * Fetch profile photos for archivedAgents in one backend call.
   */
  getArchivedAgentsPhoto() {
    const ids = this.archivedAgents
      .filter((agent: any) => agent?.agentProfilePhoto?.id)
      .map((agent: any) => agent.agentProfilePhoto.id)
    if (ids.length === 0) {
      return
    }
    this.agentsService.getProfilePhotos(ids).subscribe({
      next: (photoMap: Record<number, string>) => {
        this.archivedAgents.forEach((agent: any) => {
          const id = agent?.agentProfilePhoto?.id
          if (id && photoMap[id]) {
            agent.agentProfilePhoto['photoBlob'] = photoMap[id]
            agent.profileImageSrc = this.agentsService.convertImagetoUrl(photoMap[id])
          } else {
            agent.profileImageSrc = null
          }
        })
        // Re-apply filters to keep view consistent but do not overwrite with raw lists
        this.applyAllFilters(this.search.value)
      },
      error: (err) => console.error('Failed to load photos', err)
    })
  }

  /**
   * Fetch profile photos for allAgents in one backend call.
   */
  getAllAgentsPhoto() {
    const ids = this.allAgents
      .filter((agent: any) => agent?.agentProfilePhoto?.id)
      .map((agent: any) => agent.agentProfilePhoto.id)
    if (ids.length === 0) {
      return
    }
    this.agentsService.getProfilePhotos(ids).subscribe({
      next: (photoMap: Record<number, string>) => {
        this.allAgents.forEach((agent: any) => {
          const id = agent?.agentProfilePhoto?.id
          if (id && photoMap[id]) {
            agent.agentProfilePhoto['photoBlob'] = photoMap[id]
            agent.profileImageSrc = this.agentsService.convertImagetoUrl(photoMap[id])
          } else {
            agent.profileImageSrc = null
          }
        })
        // Re-apply filters to keep view consistent but do not overwrite with raw lists
        this.applyAllFilters(this.search.value)
      },
      error: (err) => console.error('Failed to load photos', err)
    })
  }
}
